import type { PortfolioAssetPurpose } from '@repo/api-contracts';
import { useEffect, useRef, useState } from 'react';
import { trpc } from '@/shared/api';
import { usePresignedImageUpload } from '@/shared/lib';

export interface PortfolioUploadPhoto {
  readonly localId: string;
  readonly status: 'PENDING' | 'READY' | 'FAILED';
  readonly previewUrl: string;
  readonly assetId?: string;
  readonly error?: string;
}
interface UploadEntry extends PortfolioUploadPhoto {
  file: File;
  objectUrl: string;
  uploadId?: string;
  removed: boolean;
}
interface UploadOptions {
  readonly createId?: string;
  readonly onCreateToken?: (token: string) => void;
}

/** 파일을 순차 준비하고 제거된 항목의 늦은 완료도 신규 자산만 정리한다. */
export const usePortfolioUpload = (purpose: PortfolioAssetPurpose, options: UploadOptions = {}) => {
  const [photos, setPhotos] = useState<PortfolioUploadPhoto[]>([]);
  const entries = useRef<UploadEntry[]>([]);
  const active = useRef<UploadEntry | null>(null);
  const queue = useRef<Promise<void>>(Promise.resolve());
  const ownedAssets = useRef(new Set<string>());
  const preserveAssets = useRef(false);
  const mounted = useRef(true);
  const presign = trpc.portfolioManagement.uploads.presign.useMutation();
  const confirm = trpc.portfolioManagement.uploads.confirm.useMutation();
  const discard = trpc.portfolioManagement.uploads.discard.useMutation();
  const discardRequest = useRef(discard.mutateAsync);
  discardRequest.current = discard.mutateAsync;
  const publish = () => {
    if (mounted.current) setPhotos(entries.current.filter((entry) => !entry.removed));
  };
  const dispose = async (ids: string[]) => {
    if (!ids.length) return;
    try {
      await discardRequest.current({ assets: ids.map((assetId) => ({ assetId })) });
    } catch {
      /* 이탈 요청 도착은 보장되지 않으며 서버 정기 정리가 보완한다. */
    }
  };
  const direct = usePresignedImageUpload(async (contentType) => {
    const response = await presign.mutateAsync({
      purpose,
      contentType,
      ...(options.createId ? { createId: options.createId } : {}),
    });
    if (active.current) active.current.uploadId = response.uploadId;
    if (response.createToken) options.onCreateToken?.(response.createToken);
    // S3 key를 공개하지 않는 API의 uploadId를 공유 helper의 반환 locator로 쓴다.
    return { uploadUrl: response.uploadUrl, key: response.uploadId };
  });

  const prepare = (entry: UploadEntry): Promise<void> => {
    const run = async () => {
      if (entry.removed) return;
      active.current = entry;
      try {
        if (!entry.uploadId) {
          const uploadId = await direct.upload(entry.file);
          if (!uploadId) {
            entry.uploadId = undefined;
            throw new Error(
              '파일 업로드에 실패했습니다. 형식·10MiB 제한을 확인하고 만료된 URL은 다시 전송해주세요.',
            );
          }
        }
        if (!entry.uploadId) throw new Error('업로드를 다시 전송해주세요.');
        const response = await confirm.mutateAsync({ uploadId: entry.uploadId });
        ownedAssets.current.add(response.asset.assetId);
        if (entry.removed || !mounted.current) {
          ownedAssets.current.delete(response.asset.assetId);
          await dispose([response.asset.assetId]);
        } else {
          Object.assign(entry, {
            status: 'READY',
            assetId: response.asset.assetId,
            previewUrl: response.previewUrl,
            error: undefined,
          });
        }
      } catch (cause) {
        const code =
          typeof cause === 'object' && cause !== null && 'data' in cause
            ? (cause as { data?: { code?: string } }).data?.code
            : undefined;
        if (code === 'NOT_FOUND' || code === 'BAD_REQUEST') entry.uploadId = undefined;
        Object.assign(entry, {
          status: 'FAILED',
          error:
            cause instanceof Error
              ? cause.message
              : '처리 실패. 만료된 업로드는 파일을 다시 전송해주세요.',
        });
      } finally {
        active.current = null;
        publish();
      }
    };
    queue.current = queue.current.then(run, run);
    return queue.current;
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      for (const entry of entries.current) {
        if (!entry.removed) URL.revokeObjectURL(entry.objectUrl);
        entry.removed = true;
      }
      const ids = [...ownedAssets.current];
      ownedAssets.current.clear();
      if (ids.length && !preserveAssets.current)
        void discardRequest
          .current({ assets: ids.map((assetId) => ({ assetId })) })
          .catch(() => {});
    };
  }, []);

  const remove = (localId: string) => {
    const entry = entries.current.find((value) => value.localId === localId && !value.removed);
    if (!entry) return;
    entry.removed = true;
    URL.revokeObjectURL(entry.objectUrl);
    if (entry.assetId && ownedAssets.current.delete(entry.assetId)) void dispose([entry.assetId]);
    publish();
  };
  return {
    photos,
    upload: (file: File): Promise<void> => {
      const objectUrl = URL.createObjectURL(file);
      const entry: UploadEntry = {
        localId: crypto.randomUUID(),
        status: 'PENDING',
        previewUrl: objectUrl,
        objectUrl,
        file,
        removed: false,
      };
      entries.current.push(entry);
      publish();
      return prepare(entry);
    },
    retry: (localId: string): Promise<void> => {
      const entry = entries.current.find((value) => value.localId === localId && !value.removed);
      if (!entry || entry.status === 'PENDING') return Promise.resolve();
      Object.assign(entry, { status: 'PENDING', error: undefined });
      publish();
      return prepare(entry);
    },
    remove,
    discardNewAssets: async () => {
      for (const entry of entries.current) remove(entry.localId);
      await queue.current;
    },
    releaseSavedAssets: (ids: readonly string[]) => {
      for (const id of ids) ownedAssets.current.delete(id);
    },
    // 진행/결과 불확실 저장과 이탈 정리가 경합하지 않게 둔다. 잔여 자산은 GC가 처리한다.
    preserveOnUnmount: () => {
      preserveAssets.current = true;
    },
  };
};
